import useClaimCreateStore from '@/store/claimCreateStore';
import { IMember } from '@/types';
import { getPatientDetails } from '@/utils';
import clsx from 'clsx';
import { useEffect } from 'react';
import {
  SelectMenu,
  SelectMenuButton,
  SelectMenuLabel,
  SelectMenuOption,
  SelectMenuOptions,
  SelectWrapper,
} from 'ui/ds/SelectMenu';
import { ArrowDown } from 'ui/icons';

interface IProps {
  value?: string;
  onChange: (patient: IMember | undefined, patientId: string) => void;
}

const PatientSelect = ({ value, onChange }: IProps) => {
  const memberDetails = useClaimCreateStore((state) => state.memberDetails);

  useEffect(() => {
    // doesn't seem to work w/o setTimeout
    setTimeout(() => {
      if (memberDetails?.length === 1 && !value) {
        const patient = memberDetails[0];
        onChange(patient, getPatientDetails(patient ?? {})?.id ?? '');
      }
    }, 0);
  }, [memberDetails, onChange, value]);

  return (
    <SelectMenu
      onChange={(patientId: string) => {
        const activePatient = memberDetails?.find((member) => {
          return getPatientDetails(member).id === patientId;
        });
        onChange(activePatient, patientId);
      }}
      value={value}
      className="w-full"
    >
      {({ open, value: currrentValue }: { open: boolean; value: unknown }) => {
        const activePatient = memberDetails?.find((member) => {
          return getPatientDetails(member).id === currrentValue;
        });
        const { fullName = null } = activePatient
          ? getPatientDetails(activePatient)
          : {};
        return (
          <SelectWrapper>
            <SelectMenuLabel className="block text-sm font-medium text-mobile-gray-500">
              Claimant name<span className="text-mobile-hero-100">*</span>
            </SelectMenuLabel>
            <SelectMenuButton
              trailingIcon={() => (
                <ArrowDown
                  className={clsx({
                    'rotate-180': open,
                  })}
                />
              )}
              className={clsx({
                'h-12 cursor-pointer mt-2 border border-mobile-gray-300 !rounded-[10px] outline-none focus:border-[1px] text-mobile-gray-400 !text-base !shadow-none px-4':
                  true,
                'text-mobile-gray-600': Boolean(currrentValue),
              })}
            >
              <div>{fullName ?? 'Select a claimant'}</div>
            </SelectMenuButton>
            <SelectMenuOptions className="mt-1" open={open}>
              {memberDetails?.map?.((patient) => {
                const {
                  age,
                  fullName,
                  icon: Icon,
                  relationship,
                  id,
                } = getPatientDetails(patient);
                return (
                  <SelectMenuOption key={id} value={id}>
                    {({ active }) => {
                      return (
                        <div
                          className={clsx({
                            'flex cursor-pointer py-3 px-6 gap-3': true,
                            'bg-secondary-100': active,
                          })}
                        >
                          {Icon && <Icon className="w-12 h-12" />}
                          <div className="flex flex-col gap-1">
                            <span className="text-gray-600 text-base leading-[150%]">
                              {fullName}
                            </span>
                            <span className="text-mobile-text-hierarchy-1 text-sm leading-[150%]">
                              {relationship} &bull; {age} year
                              {age ?? 0 > 1 ? 's' : ''}
                            </span>
                          </div>
                        </div>
                      );
                    }}
                  </SelectMenuOption>
                );
              })}
            </SelectMenuOptions>
          </SelectWrapper>
        );
      }}
    </SelectMenu>
  );
};

export default PatientSelect;
